import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { formatDate } from "../dashboardHelpers";
import RowShell from "./RowShell";

function PackageRow({ pkg, onPayNow, payLoadingId, onViewUsage }) {
  const isUnpaid = pkg.invoiceStatus === "UNPAID";
  const isMembership = pkg.kind === "membership";
  // Memberships grant access rather than sessions, so there are no credits to
  // account for and nothing to show.
  const canViewUsage = !isMembership && !!onViewUsage;
  const reservedSessions = isMembership ? 0 : parseInt(pkg.sessionsReserved, 10) || 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const kindChip = (
    <Chip
      size="small"
      label={isMembership ? "Membership" : "Package"}
      variant="outlined"
      sx={{
        // Matches the status chip where the two stack; stays smaller on desktop,
        // where it sits inline against the name rather than beside a pill.
        height: isMobile ? 22 : 18,
        fontSize: isMobile ? "0.7rem" : "0.65rem",
        fontWeight: 600,
        letterSpacing: 0.3,
        color: "primary.main",
        borderColor: "primary.main"
      }}
    />
  );

  const statusChip = (
    <Chip
      size="small"
      label={pkg.status}
      color={pkg.status === "active" ? "success" : pkg.status === "frozen" ? "warning" : "default"}
      variant="outlined"
      sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
    />
  );

  return (
    <RowShell onClick={canViewUsage ? () => onViewUsage(pkg) : undefined}>
      {/* Text and actions share one column so the arrow stays a direct child of
          the row and centres against its full height, not just the text line.
          RowShell's own `stack` can't do that — it would put the arrow on a
          third line of its own. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.25, sm: 2 }
        }}
      >
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.25 }}>
            <Typography variant="subtitle2" noWrap>
              {pkg.name}
            </Typography>
            {/* On a phone both chips stack on the right instead, so the name gets the full width. */}
            {!isMobile && kindChip}
          </Box>
          <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500 }}>
            {isMembership
              ? `${pkg.hasMemberAccess ? "Member pricing access" : "Active"} — ${pkg.noExpiry ? "No expiry" : `Expires ${formatDate(pkg.expiryDate)}`}`
              : `Remaining: ${pkg.sessionsRemaining} sessions — ${pkg.noExpiry ? "No expiry" : `Expires ${formatDate(pkg.expiryDate)}`}`}
          </Typography>
          {/* A pack that booked every session at purchase already reads zero remaining.
              Without this the client is told a pack they have not played is used up. */}
          {reservedSessions > 0 && (
            <Typography variant="body2" sx={{ color: "info.main" }}>
              {`${reservedSessions} ${reservedSessions === 1 ? "session" : "sessions"} booked, awaiting payment`}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            flexShrink: 0,
            justifyContent: { xs: "flex-start", sm: "flex-end" }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {isMobile && kindChip}
          {statusChip}
          {isUnpaid && pkg.invoiceId && (
            <Button
              size="small"
              variant="contained"
              disabled={payLoadingId === pkg._id}
              onClick={() => onPayNow(pkg.invoiceId, pkg._id)}
              sx={{
                // Leads the action line so the one thing worth tapping isn't
                // sitting behind two passive chips.
                order: -1,
                height: "1.75rem",
                px: 1.5,
                flexShrink: 0,
                textTransform: "none",
                color: "common.white",
                boxShadow: "none"
              }}
            >
              {payLoadingId === pkg._id ? <CircularProgress size={16} /> : "Pay Now"}
            </Button>
          )}
        </Box>
      </Box>
      {/* Rows rely on hover alone to signal they open; a phone has no hover, so
          the arrow is what says there's more to see. */}
      {canViewUsage && <ChevronRightIcon sx={{ fontSize: "1.25rem", color: "text.disabled", flexShrink: 0, ml: -0.5 }} />}
    </RowShell>
  );
}

export default PackageRow;
